import { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  Package, AlertTriangle, ArrowLeftRight, IndianRupee, Building2, Search,
  TrendingDown, BarChart3, Plus, Layers, Bell, ArrowRight, RefreshCw
} from "lucide-react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from "recharts";

// ---- Types ----
interface BranchStockItem {
  id: string;
  itemId?: string;
  branchId: string;
  name: string;
  hsnCode: string;
  stockGroup: string;
  unit: string;
  currentQty: number;
  currentRate: number;
  currentValue: number;
  reorderLevel: number;
  gstRate: number;
}

interface BranchStockTransfer {
  id: string;
  date: string;
  fromBranchId: string;
  toBranchId: string;
  itemName: string;
  hsnCode: string;
  quantity: number;
  rate: number;
  value: number;
  status: "Pending" | "In Transit" | "Received" | "Rejected";
  reference: string;
  narration: string;
  createdBy: string;
}

interface LowStockAlert {
  branchId: string;
  branchName: string;
  itemName: string;
  currentQty: number;
  reorderLevel: number;
  deficit: number;
  severity: "critical" | "warning";
}

// ---- Seed Data ----
function generateBranchStock(branchId: string, scale: number): BranchStockItem[] {
  const items = [
    { name: "Steel Bars (TMT 12mm)", hsn: "7214", group: "Raw Materials", unit: "KG", baseQty: 500, rate: 62, reorder: 100, gst: 18 },
    { name: "Copper Wire (2.5mm)", hsn: "7408", group: "Raw Materials", unit: "KG", baseQty: 200, rate: 120, reorder: 50, gst: 18 },
    { name: "Cement (OPC 53 Grade)", hsn: "2523", group: "Raw Materials", unit: "Bags", baseQty: 150, rate: 380, reorder: 30, gst: 28 },
    { name: "PVC Pipes (4 inch)", hsn: "3917", group: "Finished Goods", unit: "PCS", baseQty: 80, rate: 480, reorder: 15, gst: 18 },
    { name: "Aluminium Sheets (1mm)", hsn: "7606", group: "Raw Materials", unit: "KG", baseQty: 180, rate: 220, reorder: 40, gst: 18 },
    { name: "Paint (Exterior Emulsion)", hsn: "3210", group: "Finished Goods", unit: "LTR", baseQty: 120, rate: 350, reorder: 25, gst: 28 },
    { name: "Electrical Switches", hsn: "8536", group: "Finished Goods", unit: "PCS", baseQty: 300, rate: 85, reorder: 60, gst: 18 },
    { name: "Glass Panels (6mm)", hsn: "7005", group: "Raw Materials", unit: "SQM", baseQty: 60, rate: 950, reorder: 10, gst: 18 },
  ];

  return items.map((item, idx) => {
    const qty = Math.round(item.baseQty * scale * (0.6 + Math.random() * 0.8));
    return {
      id: `bsi-${branchId}-${idx}`,
      branchId,
      name: item.name,
      hsnCode: item.hsn,
      stockGroup: item.group,
      unit: item.unit,
      currentQty: qty,
      currentRate: item.rate,
      currentValue: qty * item.rate,
      reorderLevel: item.reorder,
      gstRate: item.gst,
    };
  });
}

const SEED_BRANCH_STOCK: BranchStockItem[] = [];
const SEED_TRANSFERS: BranchStockTransfer[] = [];

const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];
const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  Received: "default", "In Transit": "secondary", Pending: "outline", Rejected: "destructive",
};

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function BranchInventoryPage() {
  const { branches } = useBranch();
  const { user, role } = useAuth();
  const { activeCompany } = useCompany();
  const { toast } = useToast();

  const [branchStock, setBranchStock] = useState<BranchStockItem[]>([]);
  const [stockTransfers, setStockTransfers] = useState<BranchStockTransfer[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Transfer form
  const [tf, setTf] = useState({ fromBranchId: "", toBranchId: "", itemId: "", itemName: "", hsnCode: "", quantity: 0, rate: 0, narration: "" });

  const activeBranches = branches.filter((b) => b.isActive);
  const getBranchName = (id: string) => branches.find((b) => b.id === id)?.name || id;

  const loadBranchStock = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = activeCompany?.id || user?.company || "";
      if (!companyId) return;
      const res = await fetch(`/api/inventory/branch-stock?companyId=${companyId}`, {
        headers: { "x-company-id": companyId },
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setBranchStock(json.data);
        }
      }
    } catch (err) {
      console.error("Error loading branch stock:", err);
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id, user?.company]);

  useEffect(() => {
    loadBranchStock();
  }, [loadBranchStock]);

  // Filtered stock
  const filteredStock = useMemo(() => {
    let items = branchStock;
    if (selectedBranch !== "all") items = items.filter((i) => i.branchId === selectedBranch);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(q) || i.hsnCode.includes(q) || i.stockGroup.toLowerCase().includes(q));
    }
    return items;
  }, [branchStock, selectedBranch, search]);

  // Low stock alerts
  const lowStockAlerts = useMemo((): LowStockAlert[] => {
    return branchStock
      .filter((i) => i.currentQty <= i.reorderLevel)
      .map((i) => ({
        branchId: i.branchId,
        branchName: getBranchName(i.branchId),
        itemName: i.name,
        currentQty: i.currentQty,
        reorderLevel: i.reorderLevel,
        deficit: i.reorderLevel - i.currentQty,
        severity: i.currentQty <= i.reorderLevel * 0.5 ? "critical" as const : "warning" as const,
      }))
      .sort((a, b) => b.deficit - a.deficit);
  }, [branchStock, branches]);

  // Branch valuation summary
  const branchValuation = useMemo(() => {
    return activeBranches.map((b) => {
      const items = branchStock.filter((i) => i.branchId === b.id);
      return {
        name: b.name.split(" ")[0],
        branchId: b.id,
        totalItems: items.length,
        totalQty: items.reduce((s, i) => s + i.currentQty, 0),
        totalValue: items.reduce((s, i) => s + i.currentValue, 0),
        lowStock: items.filter((i) => i.currentQty <= i.reorderLevel).length,
      };
    });
  }, [activeBranches, branchStock]);

  // Stock group distribution
  const groupDistribution = useMemo(() => {
    const items = selectedBranch === "all" ? branchStock : branchStock.filter((i) => i.branchId === selectedBranch);
    const map: Record<string, number> = {};
    items.forEach((i) => { map[i.stockGroup] = (map[i.stockGroup] || 0) + i.currentValue; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [branchStock, selectedBranch]);

  // Totals
  const totals = useMemo(() => {
    const items = selectedBranch === "all" ? branchStock : branchStock.filter((i) => i.branchId === selectedBranch);
    return {
      items: items.length,
      value: items.reduce((s, i) => s + i.currentValue, 0),
      lowStock: items.filter((i) => i.currentQty <= i.reorderLevel).length,
      pendingTransfers: stockTransfers.filter((t) => t.status === "Pending" || t.status === "In Transit").length,
    };
  }, [branchStock, stockTransfers, selectedBranch]);

  const handleCreateTransfer = useCallback(async () => {
    if (!tf.fromBranchId || !tf.toBranchId || tf.fromBranchId === tf.toBranchId) {
      toast({ title: "Invalid", description: "Select different source and destination branches.", variant: "destructive" });
      return;
    }
    if (!tf.itemName || tf.quantity <= 0) {
      toast({ title: "Invalid", description: "Fill item name and quantity.", variant: "destructive" });
      return;
    }

    try {
      const companyId = activeCompany?.id || user?.company || "";
      const matchedItem = branchStock.find((i) => (tf.itemId && i.itemId === tf.itemId) || i.name === tf.itemName);
      const stockItemId = tf.itemId || matchedItem?.itemId || matchedItem?.id;

      const res = await fetch("/api/inventory/branch-transfer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-company-id": companyId,
        },
        body: JSON.stringify({
          companyId,
          fromBranchId: tf.fromBranchId,
          toBranchId: tf.toBranchId,
          stockItemId,
          quantity: tf.quantity,
          rate: tf.rate,
          narration: tf.narration,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Failed to create branch transfer");
      }

      const transfer: BranchStockTransfer = {
        id: `bst-${crypto.randomUUID().slice(0, 8)}`,
        date: new Date().toISOString().split("T")[0],
        fromBranchId: tf.fromBranchId,
        toBranchId: tf.toBranchId,
        itemName: tf.itemName,
        hsnCode: tf.hsnCode,
        quantity: tf.quantity,
        rate: tf.rate,
        value: tf.quantity * tf.rate,
        status: "Received",
        reference: json.data?.reference || `BST/${new Date().getFullYear()}/${String(stockTransfers.length + 1).padStart(3, "0")}`,
        narration: tf.narration,
        createdBy: user?.name || "User",
      };

      setStockTransfers((prev) => [transfer, ...prev]);
      await loadBranchStock();
      setTransferOpen(false);
      setTf({ fromBranchId: "", toBranchId: "", itemId: "", itemName: "", hsnCode: "", quantity: 0, rate: 0, narration: "" });
      toast({ title: "Branch Transfer Completed", description: `Transferred ${tf.quantity} units between branches.` });
    } catch (err: any) {
      toast({ title: "Transfer Failed", description: err.message, variant: "destructive" });
    }
  }, [tf, user, activeCompany?.id, branchStock, loadBranchStock, stockTransfers.length, toast]);

  const updateTransferStatus = (id: string, status: BranchStockTransfer["status"]) => {
    setStockTransfers((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      // If marking received, update branch stock
      if (status === "Received") {
        setBranchStock((stock) => {
          const updated = [...stock];
          // Decrease from source
          const srcIdx = updated.findIndex((s) => s.branchId === t.fromBranchId && s.name === t.itemName);
          if (srcIdx >= 0) {
            updated[srcIdx] = { ...updated[srcIdx], currentQty: Math.max(0, updated[srcIdx].currentQty - t.quantity), currentValue: Math.max(0, updated[srcIdx].currentQty - t.quantity) * updated[srcIdx].currentRate };
          }
          // Increase at destination
          const destIdx = updated.findIndex((s) => s.branchId === t.toBranchId && s.name === t.itemName);
          if (destIdx >= 0) {
            updated[destIdx] = { ...updated[destIdx], currentQty: updated[destIdx].currentQty + t.quantity, currentValue: (updated[destIdx].currentQty + t.quantity) * updated[destIdx].currentRate };
          }
          return updated;
        });
      }
      return { ...t, status };
    }));
    toast({ title: `Transfer ${status.toLowerCase()}` });
  };

  // Items from selected branch for transfer dropdown
  const sourceItems = useMemo(() => {
    if (!tf.fromBranchId) return [];
    return branchStock.filter((i) => i.branchId === tf.fromBranchId && i.currentQty > 0);
  }, [tf.fromBranchId, branchStock]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Branch Inventory</h1>
          <p className="text-muted-foreground">Track stock levels, transfers, and valuation across all branches</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {activeBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
            <DialogTrigger asChild>
              <Button><ArrowLeftRight className="mr-2 h-4 w-4" /> Transfer Stock</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Inter-Branch Stock Transfer</DialogTitle>
                <DialogDescription>Transfer inventory between branches. Stock levels update on receipt.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Branch</Label>
                    <Select value={tf.fromBranchId} onValueChange={(v) => setTf((p) => ({ ...p, fromBranchId: v, itemName: "", hsnCode: "" }))}>
                      <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                      <SelectContent>{activeBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>To Branch</Label>
                    <Select value={tf.toBranchId} onValueChange={(v) => setTf((p) => ({ ...p, toBranchId: v }))}>
                      <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
                      <SelectContent>{activeBranches.filter((b) => b.id !== tf.fromBranchId).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Item</Label>
                  {sourceItems.length > 0 ? (
                    <Select value={tf.itemName} onValueChange={(v) => {
                      const item = sourceItems.find((i) => i.name === v);
                      setTf((p) => ({ ...p, itemName: v, itemId: item?.itemId || "", hsnCode: item?.hsnCode || "", rate: item?.currentRate || 0 }));
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>{sourceItems.map((i) => (
                        <SelectItem key={i.id} value={i.name}>{i.name} ({i.currentQty} {i.unit})</SelectItem>
                      ))}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={tf.itemName} onChange={(e) => setTf((p) => ({ ...p, itemName: e.target.value }))} placeholder="Item name" />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>HSN Code</Label>
                    <Input value={tf.hsnCode} onChange={(e) => setTf((p) => ({ ...p, hsnCode: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input type="number" min={1} value={tf.quantity} onChange={(e) => setTf((p) => ({ ...p, quantity: +e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Rate (₹)</Label>
                    <Input type="number" min={0} value={tf.rate} onChange={(e) => setTf((p) => ({ ...p, rate: +e.target.value }))} />
                  </div>
                </div>
                <div className="text-right text-sm font-medium">Value: {fmt(tf.quantity * tf.rate)}</div>
                <div className="space-y-2">
                  <Label>Narration</Label>
                  <Textarea value={tf.narration} onChange={(e) => setTf((p) => ({ ...p, narration: e.target.value }))} placeholder="Reason for transfer..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateTransfer}>Create Transfer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Package className="h-5 w-5 text-primary" /></div>
            <div><p className="text-sm text-muted-foreground">Total Items</p><p className="text-2xl font-bold">{totals.items}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-chart-2/10"><IndianRupee className="h-5 w-5 text-chart-2" /></div>
            <div><p className="text-sm text-muted-foreground">Stock Valuation</p><p className="text-2xl font-bold">{fmt(totals.value)}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10"><TrendingDown className="h-5 w-5 text-destructive" /></div>
            <div><p className="text-sm text-muted-foreground">Low Stock Items</p><p className="text-2xl font-bold">{totals.lowStock}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-chart-3/10"><ArrowLeftRight className="h-5 w-5 text-chart-3" /></div>
            <div><p className="text-sm text-muted-foreground">Pending Transfers</p><p className="text-2xl font-bold">{totals.pendingTransfers}</p></div>
          </div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="stock" className="space-y-4">
        <TabsList>
          <TabsTrigger value="stock"><Package className="mr-1 h-4 w-4" /> Stock Levels</TabsTrigger>
          <TabsTrigger value="transfers"><ArrowLeftRight className="mr-1 h-4 w-4" /> Transfers</TabsTrigger>
          <TabsTrigger value="valuation"><BarChart3 className="mr-1 h-4 w-4" /> Valuation</TabsTrigger>
          <TabsTrigger value="alerts"><Bell className="mr-1 h-4 w-4" /> Alerts ({lowStockAlerts.length})</TabsTrigger>
        </TabsList>

        {/* STOCK LEVELS */}
        <TabsContent value="stock" className="space-y-4">
          <div className="flex items-center gap-2 max-w-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items, HSN, group..." className="h-9" />
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Branch</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>HSN</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredStock.map((item) => {
                    const isLow = item.currentQty <= item.reorderLevel;
                    const isCritical = item.currentQty <= item.reorderLevel * 0.5;
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm">{getBranchName(item.branchId).split(" ")[0]}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-muted-foreground text-xs font-mono">{item.hsnCode}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{item.stockGroup}</Badge></TableCell>
                        <TableCell className="text-right font-medium">{item.currentQty} {item.unit}</TableCell>
                        <TableCell className="text-right">{fmt(item.currentRate)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(item.currentValue)}</TableCell>
                        <TableCell>
                          {isCritical ? <Badge variant="destructive">Critical</Badge>
                            : isLow ? <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Low</Badge>
                            : <Badge variant="secondary">OK</Badge>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredStock.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No items found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRANSFERS */}
        <TabsContent value="transfers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Branch Stock Transfers</CardTitle>
              <CardDescription>Track inventory movement between branches</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {stockTransfers.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono text-xs">{t.reference}</TableCell>
                      <TableCell>{t.date}</TableCell>
                      <TableCell>
                        <span className="font-medium">{getBranchName(t.fromBranchId).split(" ")[0]}</span>
                        <ArrowRight className="inline h-3 w-3 mx-1 text-muted-foreground" />
                        <span className="font-medium">{getBranchName(t.toBranchId).split(" ")[0]}</span>
                      </TableCell>
                      <TableCell>{t.itemName}</TableCell>
                      <TableCell className="text-right">{t.quantity}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(t.value)}</TableCell>
                      <TableCell><Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge></TableCell>
                      <TableCell>
                        {t.status === "Pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => updateTransferStatus(t.id, "In Transit")}>Dispatch</Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateTransferStatus(t.id, "Rejected")}>Reject</Button>
                          </div>
                        )}
                        {t.status === "In Transit" && (
                          <Button size="sm" variant="outline" onClick={() => updateTransferStatus(t.id, "Received")}>Receive</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VALUATION */}
        <TabsContent value="valuation" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Branch-wise Stock Valuation</CardTitle>
                <CardDescription>Total inventory value per branch</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={branchValuation}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} className="text-xs" />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Bar dataKey="totalValue" fill="hsl(var(--primary))" name="Stock Value" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stock Group Distribution</CardTitle>
                <CardDescription>{selectedBranch === "all" ? "All branches" : getBranchName(selectedBranch)}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={groupDistribution} cx="50%" cy="50%" outerRadius={110} dataKey="value" nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {groupDistribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Branch comparison cards */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Branch Inventory Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {branchValuation.map((bv, idx) => (
                    <Card key={bv.branchId} className="border-border/50">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                          <span className="font-semibold text-sm">{bv.name}</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-muted-foreground">Items</span><span className="font-medium">{bv.totalItems}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Total Qty</span><span className="font-medium">{bv.totalQty.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Valuation</span><span className="font-bold">{fmt(bv.totalValue)}</span></div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Low Stock</span>
                            <Badge variant={bv.lowStock > 0 ? "destructive" : "secondary"} className="text-xs">{bv.lowStock}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ALERTS */}
        <TabsContent value="alerts" className="space-y-4">
          {lowStockAlerts.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">All stock levels are healthy</p>
              <p className="text-sm">No items below reorder level across any branch.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      <div>
                        <p className="font-semibold text-destructive">Critical Alerts</p>
                        <p className="text-2xl font-bold">{lowStockAlerts.filter((a) => a.severity === "critical").length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="font-semibold text-amber-600">Warning Alerts</p>
                        <p className="text-2xl font-bold">{lowStockAlerts.filter((a) => a.severity === "warning").length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Severity</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Current Qty</TableHead>
                      <TableHead className="text-right">Reorder Level</TableHead>
                      <TableHead className="text-right">Deficit</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {lowStockAlerts.map((alert, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge variant={alert.severity === "critical" ? "destructive" : "outline"}
                              className={alert.severity === "warning" ? "border-amber-500/30 text-amber-600" : ""}>
                              {alert.severity === "critical" ? "Critical" : "Warning"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{alert.branchName.split(" ")[0]}</TableCell>
                          <TableCell>{alert.itemName}</TableCell>
                          <TableCell className="text-right font-medium">{alert.currentQty}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{alert.reorderLevel}</TableCell>
                          <TableCell className="text-right font-medium text-destructive">-{alert.deficit}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => {
                              setTf((p) => ({ ...p, toBranchId: alert.branchId, itemName: alert.itemName }));
                              setTransferOpen(true);
                            }}>
                              <ArrowLeftRight className="h-3 w-3 mr-1" /> Transfer In
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
